import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getMyListings, cancelListing } from '@/api/listing';
import TabWithFilter from '@/components/TabWithFilter';
import ListingCard from '@/components/ListingCard';
import ConfirmDialog from '@/components/ConfirmDialog';
import Button from '@/components/Button';
import dondonGreyLogo from '@/assets/dondon_grey.svg';

const LISTINGS_STATE_KEY = 'listings_page_state';

const getListingKey = (listing) => {
  if (listing?.id) return listing.id;
  const productId = listing?.product?.id ?? 'unknown';
  const price = listing?.price ?? '0';
  return `${productId}-${price}`;
};

const ListingsPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [accumulatedListings, setAccumulatedListings] = useState([]);
  const [cachedNextCursor, setCachedNextCursor] = useState(null);
  const [hasMoreFromCache, setHasMoreFromCache] = useState(false);
  const [restoredFromCache, setRestoredFromCache] = useState(false);
  const restoredEntriesRef = useRef(false);

  const { data: listingsData, isLoading, isError, isFetching } = useQuery({
    queryKey: ['listings', dateRange.startDate, dateRange.endDate, cursor],
    queryFn: () => getMyListings({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      cursor
    })
  });

  // Restore cached listings/pagination state on mount
  useEffect(() => {
    const cachedState = sessionStorage.getItem(LISTINGS_STATE_KEY);
    if (cachedState) {
      try {
        const parsed = JSON.parse(cachedState);
        if (Array.isArray(parsed.accumulated)) {
          setAccumulatedListings(parsed.accumulated);
          restoredEntriesRef.current = parsed.accumulated.length > 0;
        }
        if (typeof parsed.nextCursor === 'string' || parsed.nextCursor === null) {
          setCachedNextCursor(parsed.nextCursor);
        }
        if (typeof parsed.hasMore === 'boolean') {
          setHasMoreFromCache(parsed.hasMore);
        }
        if (parsed.dateRange) {
          setDateRange({
            startDate: parsed.dateRange.startDate || null,
            endDate: parsed.dateRange.endDate || null
          });
        }
      } catch (error) {
        console.warn('Failed to parse listings cache', error);
        sessionStorage.removeItem(LISTINGS_STATE_KEY);
      }
    }
    setRestoredFromCache(true);
  }, []);

  // Clear cached state on full page reload/close
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem(LISTINGS_STATE_KEY);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Update accumulated listings when new data arrives
  useEffect(() => {
    if (!listingsData?.data) {
      return;
    }

    const incoming = listingsData.data;
    const incomingIds = new Set(incoming.map(getListingKey));
    const shouldPreserveCachedMeta = restoredEntriesRef.current && cursor === null;

    setAccumulatedListings((prev) => {
      // cursor === null means we are handling the first page
      if (!cursor) {
        if (!restoredEntriesRef.current) {
          // Not restored from cache (for example, listings_page_state was cleared on another page):
          // this is a fresh entry into ListingsPage, so rely only on server data
          // and avoid keeping cancelled listings from old cached pages.
          return [...incoming];
        }

        // Restored from cache and cursor === null (e.g. back navigation from a second page detail):
        // keep the original behavior of "first page on top, cached subsequent pages below".
        const filteredPrev = prev.filter((item) => !incomingIds.has(getListingKey(item)));
        return [...incoming, ...filteredPrev];
      }

      // cursor is not null: this is a subsequent page, de-duplicate then append
      const filteredPrev = prev.filter((item) => !incomingIds.has(getListingKey(item)));
      return [...filteredPrev, ...incoming];
    });

    if (!shouldPreserveCachedMeta) {
      setCachedNextCursor(listingsData.cursor ?? null);
      setHasMoreFromCache(Boolean(listingsData.cursor));
      restoredEntriesRef.current = false;
    }
  }, [listingsData, cursor]);

  // Persist cached state
  useEffect(() => {
    if (!restoredFromCache) return;
    const stateToCache = {
      accumulated: accumulatedListings,
      nextCursor: cachedNextCursor ?? null,
      hasMore: hasMoreFromCache,
      dateRange
    };
    sessionStorage.setItem(LISTINGS_STATE_KEY, JSON.stringify(stateToCache));
  }, [accumulatedListings, cachedNextCursor, hasMoreFromCache, dateRange, restoredFromCache]);

  const cancelListingMutation = useMutation({
    mutationFn: (listingId) => cancelListing(listingId),
    onSuccess: (_data, listingId) => {
      // Let React Query refetch the listings data
      queryClient.invalidateQueries({ queryKey: ['listings'] });

      // Keep local accumulatedListings in sync and remove the cancelled listing
      // so it does not reappear at the end when merging cached pages
      if (!listingId) return;
      setAccumulatedListings((prev) =>
        prev.filter((listing) => listing.id !== listingId)
      );
    }
  });

  const tabs = [
    { value: 'ALL', label: t('me.listings.subtitle'), special: true },
  ];

  const handleSearch = (dateRange) => {
    setDateRange(dateRange);
    setCursor(null); // Reset pagination when filter changes
  };

  const handleReset = () => {
    setDateRange({ startDate: null, endDate: null });
    setCursor(null); // Reset pagination when filter changes
  };

  const handleCancelListing = (listing) => {
    setSelectedListing(listing);
    setIsConfirmDialogOpen(true);
  };

  const confirmCancelListing = () => {
    if (selectedListing) {
      cancelListingMutation.mutate(selectedListing.id);
    }
    setIsConfirmDialogOpen(false);
  };

  const closeConfirmDialog = () => {
    setIsConfirmDialogOpen(false);
  };

  // Check if this is a first-time listing with reward
  const isFirstTimeListing = selectedListing?.benefitReward?.issueReward;
  const rewardAmount = selectedListing?.benefitReward?.rewardAmount;

  const nextCursorFromQuery = listingsData?.cursor ?? cachedNextCursor;
  const nextCursorToUse = restoredEntriesRef.current ? cachedNextCursor : nextCursorFromQuery;
  const hasMoreListings = restoredEntriesRef.current ? hasMoreFromCache : Boolean(nextCursorFromQuery);

  const handleLoadMore = () => {
    if (nextCursorToUse) {
      setCursor(nextCursorToUse);
      setCachedNextCursor(null);
      setHasMoreFromCache(false);
    }
  };

  // Determine if any filter is active (only date range for listings)
  const isAnyFilterActive = dateRange.startDate || dateRange.endDate;

  // Determine if the filter UI (date filter and headers) should be shown
  const shouldShowFilterUI = isAnyFilterActive || accumulatedListings.length > 0;

  return (
    <div className="mx-auto bg-white rounded-lg shadow-sm p-6">
      {/* Header */}
      <h1 className="text-lg font-bold mb-6">{t('me.menu.listings')}</h1>

      {shouldShowFilterUI && (
        <TabWithFilter
          tabs={tabs} // tabs is empty for ListingsPage, it will only show date filter
          activeTab="" // Not used visually as there are no tabs
          onTabChange={() => {}} // No tabs to change
          onSearch={handleSearch}
          onReset={handleReset}
          initialDateRange={dateRange}
        />
      )}

      {/* Table Headers */}
      {shouldShowFilterUI && (
        <div className="mt-4 grid grid-cols-[1fr,108px,108px,180px] gap-6 py-1.5 bg-[#f8f8f8] text-sm text-center text-gray-500">
          <div>{t('listings.columns.listingInfo')}</div>
          <div>{t('listings.columns.grade')}</div>
          <div>{t('listings.columns.price')}</div>
          <div>{t('listings.columns.operations')}</div>
        </div>
      )}

      {/* Listings List */}
      {isLoading && !cursor && !shouldShowFilterUI ? (
        <div className="h-[800px] flex justify-center items-center">
          <span className="text-gray-400">{t('common.loading')}</span>
        </div>
      ) : isLoading && !cursor && shouldShowFilterUI ? (
        <div className="w-full flex justify-center items-center py-10">
          <p>{t('common.loading')}</p>
        </div>
      ) : isError ? (
        <div className="w-full flex justify-center items-center py-10">
          <p>{t('common.error')}</p>
        </div>
      ) : accumulatedListings.length > 0 ? (
        <div className="divide-y divide-gray-200">
          {accumulatedListings.map((listing) => (
            <ListingCard 
              key={listing.id} 
              listing={listing} 
              onCancelListing={() => handleCancelListing(listing)}
            />
          ))}
        </div>
      ) : !shouldShowFilterUI ? (
        <div className="h-[800px] flex flex-col justify-center items-center gap-6">
          <img src={dondonGreyLogo} alt="DonDon" className="w-24 h-16 opacity-50" />
          <div className="text-center text-[#aaaaaa] text-sm font-normal leading-tight">
            {t('me.listings.empty_state_main')}
          </div>
        </div>
      ) : (
        <div className="w-full flex justify-center items-center py-10">
          <p>{t('me.listings.noListings')}</p>
        </div>
      )}

      {/* Loading indicator for pagination */}
      {isFetching && cursor && (
        <div className="w-full flex justify-center items-center py-4">
          <p>{t('common.loading')}</p>
        </div>
      )}

      {/* Load More Button */}
      {hasMoreListings && (
        <div className="flex justify-center mt-8">
          <Button 
            variant="default" 
            size="m" 
            onClick={handleLoadMore}
            disabled={isFetching}
          >
            {isFetching && cursor ? t('common.loading') : t('home.actions.load_more')}
          </Button>
        </div>
      )}

      {/* Cancel Listing Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isConfirmDialogOpen}
        onClose={closeConfirmDialog}
        onConfirm={confirmCancelListing}
        title={isFirstTimeListing ? t('listings.cancelListing.first_time_title') : t('listings.cancelListing.title')}
        description={isFirstTimeListing ? t('listings.cancelListing.first_time_description') : t('listings.cancelListing.description')}
        cancelText={isFirstTimeListing ? t('listings.cancelListing.first_time_cancel') : t('listings.cancelListing.cancel')}
        confirmText={t('listings.cancelListing.confirm')}
      />
    </div>
  );
};

export default ListingsPage; 